// src/pages/research.js

import React from 'react';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { email } from '@config';
import { useActiveSection } from '@hooks';
import { Layout } from '@components';
import {
  Callout,
  Quote,
  Note,
  Placeholder,
  Comparison,
  Figure,
  FigureSearchDirection,
  FigurePathCompression,
  FigureSelfAssembly,
  FigureFiltering,
  FigureHypergraph,
  FigureDeduction,
  FigureInduction,
  FigureAbduction,
  Eq,
  EqLine,
  MathText,
} from '@components/research';

/* The page is a spine of addressable sections with a contents rail beside it,
   not one long essay, because it is read in two directions: straight down, and
   by jumping to one section.

   Three shapes set something apart from the running text, and they are a
   closed set. See components/research/callout.js for what each one means and
   when to reach for it:

     Quote    one statement lifted out of the flow
     Callout  a labelled block that can be read on its own
     Note     one sentence attached to the line above it

   Spans in the gap colour mark a point the current paradigm cannot follow, so
   that case can be scanned on its own.

   The mechanism is unpublished and nothing here discloses it. */

const SECTIONS = [
  { id: 'overview', title: 'The Problem', nav: 'The Problem' },
  { id: 'method', title: 'Methodology', nav: 'Methodology' },
  { id: 'paradigm', title: 'Where deep learning cannot follow', nav: 'Deep learning' },
  { id: 'emergence', title: 'Structure that assembles itself', nav: 'Self assembly' },
  { id: 'neighbours', title: 'Where it sits', nav: 'Where it sits' },
  { id: 'status', title: 'Where it stands', nav: 'Where it stands' },
  { id: 'collaborate', title: 'Work with me', nav: 'Work with me' },
];

const NEIGHBOURS = [
  {
    name: 'Hebbian learning, STDP',
    shares: 'a local update rule driven by timing, with no backward pass',
    differs:
      'they operate on physical connections between neurons, while this operates on abstract states and the regions they fall into, which is a different level of description',
  },
  {
    name: 'Forward Forward',
    shares: 'genuinely no backward pass',
    differs:
      'layers are trained independently against a goodness function a designer supplies, so the objective is still fixed and still external',
  },
  {
    name: 'Predictive coding',
    shares: 'no chain rule backward pass, and an internally generated signal',
    differs:
      'it takes expectation as a primitive without defining it, keeps top down prediction and iterative settling, and converges numerically toward the gradients backpropagation would have produced',
  },
  {
    name: 'Free Energy Principle',
    shares: 'a self updating internal model with no external supervisor',
    differs:
      'it rests on an explicit generative model and variational inference, both assumed rather than derived from observation',
  },
  {
    name: 'Heteroclinic network models',
    shares: 'the vocabulary of saddles, trajectories and phase space',
    differs:
      'the network of saddles is fixed and the question is how a trajectory moves through it, whereas here the structure is itself the thing that evolves',
  },
  {
    name: 'World models, JEPA',
    shares: 'an internal model of how the environment unfolds in time',
    differs:
      'the architecture carrying that model is designed in advance, rather than emerging out of the temporal structure of the input',
  },
  {
    name: 'Global Workspace, IIT',
    shares: 'consciousness treated as the object of study rather than a side effect',
    differs:
      'they describe a functional arrangement or assign a scalar to it, without a structure that says how any of it forms or changes',
  },
];

const StyledResearchPage = styled.main`
  .inner {
    max-width: 1000px;
    margin: 0 auto;
    margin-top: -1rem;
  }

  /* Below this the contents rail is gone, and with it the only thing that was
     using the right hand two fifths of the 1000px column. Everything in the
     body caps at the reading measure, so what was left was a column of text
     pinned to the left edge with 300px of nothing beside it. Narrowing the
     whole page to the measure puts it back in the middle, and the header
     travels with it so the h1 keeps sitting over the text it introduces. */
  @media (max-width: 1080px) {
    .inner {
      max-width: var(--measure);
    }
  }
`;

const StyledHeader = styled.header`
  margin-bottom: var(--space-8);

  .eyebrow {
    display: block;
    margin-bottom: var(--space-3);
    color: var(--accent);
    font-family: var(--font-sans);
    font-size: var(--fz-xs);
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  h1 {
    margin: 0;
    font-size: clamp(34px, 6vw, 58px);
    line-height: var(--lh-display);
    letter-spacing: -0.03em;
    color: var(--text);
  }

  .intro {
    max-width: var(--measure);
    margin: var(--space-5) 0 0;
    color: var(--text-secondary);
    font-size: var(--fz-lg);
    line-height: var(--lh-body);

    strong {
      color: var(--text-emphasis);
      font-weight: 500;
    }

    @media (max-width: 480px) {
      font-size: var(--fz-lg);
    }
  }

  /* All three paragraphs are set identically. Shrinking the later ones made
     what they say read as small print, and none of it is small print. */
  .intro + .intro {
    margin-top: var(--space-4);
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-5);
    margin-top: var(--space-6);
  }

  .actions .primary {
    ${({ theme }) => theme.mixins.button};
  }

  /* Without this it inherits the body's 20px and outsizes the equivalent link
     on the home page, which is set at --fz-lg. The underline offset is matched
     too, since the mixin's default sits for body copy rather than for this
     size. */
  .actions .inline-link {
    font-size: var(--fz-lg);

    &:after {
      bottom: 0.4em;
    }
  }

  .archive-link {
    font-family: var(--font-sans);
    font-size: var(--fz-lg);
    &:after {
      bottom: 0.4em;
    }
  }
`;

const StyledBody = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  grid-gap: var(--space-9);
  align-items: start;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
    grid-gap: 0;
  }
`;

const StyledToc = styled.nav`
  position: sticky;
  top: calc(var(--nav-scroll-height) + var(--space-6));

  @media (max-width: 1080px) {
    display: none;
  }

  .label {
    display: block;
    margin-bottom: var(--space-4);
    color: var(--text-disabled);
    font-family: var(--font-sans);
    font-size: var(--fz-xxs);
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  ol {
    ${({ theme }) => theme.mixins.resetList};
  }

  /* No gap between items, so the eight border-lefts meet and read as a single
     rule running the height of the list, with the active section lighting its
     own stretch of it. */
  li {
    margin-bottom: 0;
  }

  a {
    display: block;
    /* Asymmetric, like every other label on this site. Calibre's cap band sits
       ~0.17em above the centre of its line box, so symmetric padding leaves the
       text riding high against the rule beside it. */
    padding: calc(var(--space-2) + var(--optical)) 0 calc(var(--space-2) - var(--optical))
      var(--space-4);
    border-left: 1px solid var(--border);
    color: var(--text-muted);
    font-family: var(--font-sans);
    font-size: var(--fz-sm);
    /* Tighter than --lh-body: these are two and three word nav labels, and a
       wrapped one has to stay visibly one item rather than two. */
    line-height: 1.4;
    transition: var(--transition);

    &:hover,
    &:focus-visible {
      border-left-color: var(--border-strong);
      color: var(--text-secondary);
    }

    /* Colour and a lit rule, no weight change: a bolder label is wider, and a
       rail whose items resize as you scroll never sits still. */
    &.active {
      border-left-color: var(--accent);
      color: var(--accent);
    }
  }
`;

const StyledSections = styled.div`
  min-width: 0;

  section {
    /* Overrides the global section rule, written for the home page's full
       width bands, which would put 96px of air inside this column. */
    max-width: none;
    margin: 0 0 var(--space-9);
    padding: 0;
    scroll-margin-top: calc(var(--nav-scroll-height) + var(--space-5));

    &:last-child {
      margin-bottom: 0;
    }

    @media (max-width: 768px) {
      margin-bottom: var(--space-8);
    }
  }

  h2 {
    margin: 0 0 var(--space-5);
    color: var(--text);
    font-size: clamp(24px, 3.5vw, var(--fz-heading));
    font-weight: 600;
    letter-spacing: -0.02em;
  }

  h3 {
    margin: var(--space-7) 0 var(--space-3);
    color: var(--text-emphasis);
    font-size: var(--fz-xl);
    font-weight: 500;
  }

  /* One step below h3, for a run of short subsections that each carry their
     own figure. Set at body size and separated by weight and colour rather
     than by another size step: four heading sizes inside one section stops
     reading as a hierarchy and starts reading as noise. */
  h4 {
    margin: var(--space-6) 0 var(--space-3);
    color: var(--text-emphasis);
    font-size: var(--fz-lg);
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  p {
    max-width: var(--measure);
    margin: 0 0 var(--space-5);
    color: var(--text-body);
    font-size: var(--fz-lg);
    line-height: var(--lh-body);

    &:last-child {
      margin-bottom: 0;
    }
  }

  .after-block {
    margin-top: var(--space-5);
  }

  /* Figures stop where the prose stops. Without this they run the full 724px
     column while every paragraph caps at --measure, which reads as the figure
     hanging off the right edge of the body. */
  figure {
    max-width: var(--measure);
  }

  /* Two emphases, two meanings. Accent is the phrase a passage turns on. The
     gap colour is reserved for a place the current paradigm cannot follow, so
     scanning for it alone recovers the case against it. */

  .key {
    color: var(--text-emphasis);
    font-weight: 500;
  }

  .gap {
    color: var(--gap);
  }

  /* Terms defined side by side rather than in a sentence each, so a reader can
     compare them without holding one in mind while reading the other. */
  .definitions {
    max-width: var(--measure);
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: var(--space-4);
    margin: var(--space-6) 0;

    @media (max-width: 700px) {
      grid-template-columns: 1fr;
    }
  }

  .definition {
    padding: var(--space-5) var(--space-5) var(--space-4);
    border: 1px solid var(--border);
    border-radius: var(--border-radius);
    background-color: var(--surface-transparent);
    ${({ theme }) => theme.mixins.glassPanel};

    .definition-name {
      display: block;
      margin-bottom: var(--space-3);
      color: var(--accent);
      font-family: var(--font-sans);
      font-size: var(--fz-sm);
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
    }

    p {
      max-width: none;
      margin: 0;
      color: var(--text-secondary);
      font-size: var(--fz-lg);
      line-height: var(--lh-body);
    }
  }

  .points {
    ${({ theme }) => theme.mixins.resetList};
    margin: 0 0 var(--space-5);

    li {
      max-width: var(--measure);
      position: relative;
      padding-left: var(--space-5);
      margin-bottom: var(--space-4);
      color: var(--text-body);
      font-size: var(--fz-lg);
      line-height: var(--lh-body);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--accent);
        line-height: inherit;
      }
    }
  }

  /* An ordered list where the number is part of the point being made. */
  .numbered {
    ${({ theme }) => theme.mixins.resetList};
    counter-reset: numbered;
    /* Explicit, so the space above the list does not depend on how ragged the
       last line of the paragraph before it happened to be. */
    margin: var(--space-6) 0 var(--space-5);

    li {
      max-width: var(--measure);
      position: relative;
      padding-left: var(--space-7);
      margin-bottom: var(--space-5);
      color: var(--text-body);
      font-size: var(--fz-lg);
      line-height: var(--lh-body);

      &:before {
        counter-increment: numbered;
        content: counter(numbered);
        position: absolute;
        left: 0;
        top: 0;
        width: 26px;
        height: 26px;
        display: flex;
        align-items: center;
        justify-content: center;
        /* Measured rather than guessed. Calibre reports ascent 8 and descent 5
           at 13px, and a digit has no descender, so its ink runs from the top
           of the line box to the baseline and its centre sits 2.49px ABOVE the
           line box centre. align-items:center centres the line box, so the
           digit reads high by that amount. Padding shifts the centred line box
           by half its value, hence 0.38em: twice the 0.19em ink offset.
           Tabular figures so 1 and 3 share an advance and centre alike. */
        line-height: 1;
        padding-top: 0.38em;
        font-variant-numeric: tabular-nums;
        border: 1px solid var(--border-strong);
        border-radius: 50%;
        color: var(--accent);
        font-family: var(--font-sans);
        font-size: var(--fz-xs);
        font-weight: 600;
      }

      .numbered-title {
        display: block;
        margin-bottom: var(--space-1);
        color: var(--text-emphasis);
        font-size: var(--fz-lg);
        font-weight: 500;
        line-height: var(--lh-heading);
      }
    }
  }

  .contact-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-5);
    margin-top: var(--space-6);
  }

  .contact-actions .primary {
    ${({ theme }) => theme.mixins.button};
  }

  .contact-email {
    color: var(--text-muted);
    font-size: var(--fz-md);
  }

  .placeholder-wrap {
    max-width: var(--measure);
    margin-top: var(--space-7);
  }

  .comparison {
    max-width: var(--measure);
  }
`;

const ResearchPage = ({ location }) => {
  const activeId = useActiveSection(SECTIONS.map(s => s.id));

  return (
    <Layout location={location}>
      <Helmet
        title="Research"
        meta={[
          {
            name: 'description',
            content:
              'A machine learning framework built by modelling the structure of human cognition directly, aiming at intelligence of the same kind as ours rather than extending the deep learning paradigm.',
          },
        ]}
      />

      <StyledResearchPage>
        <div className="inner">
          <span className="breadcrumb">
            <Link to="/">
              <span className="arrow">&larr; Back</span>
            </Link>
          </span>

          <StyledHeader>
            <span className="eyebrow">Research Brief</span>
            <h1>Modelling Intelligence From Its Structure</h1>

            <p className="intro">
              This research project explores the geometric structure of human cognition, and a
              machine learning framework built from that structure. It models the abstract structure
              of cognition from first principles and from observed phenomena, using as few
              assumptions as possible and none of those the current paradigm takes for granted.
            </p>
            <p className="intro">
              The work points to a machine learning algorithm that uses{' '}
              <strong>no backward pass of any kind</strong>, not only no backpropagation, and so
              opens a family of models the current paradigm has no room for. With it come a{' '}
              <strong>
                learning paradigm distinct from supervised, unsupervised and reinforcement learning
              </strong>
              , a <strong>structural basis for explainability</strong>, a{' '}
              <strong>form of information compression</strong> that deep learning cannot perform,
              and a framework that accounts for a range of psychological and philosophical phenomena
              under the same set of rules.
            </p>
            <p className="intro">
              This page covers why the problem is worth attacking, how it is approached, and some of
              what the structure turns out to imply.{' '}
              <strong>The modelling detail is not included, as the research is unpublished.</strong>{' '}
              What is here is the reasoning that produced the work and the ideas underneath it,
              which is enough to see what the work is.
            </p>

            <div className="actions">
              <a className="primary" href={`mailto:${email}`}>
                Get in touch
              </a>
              <Link className="inline-link archive-link" to="/blogs">
                Read the blogs
              </Link>
            </div>
          </StyledHeader>

          <StyledBody>
            <StyledToc aria-label="On this page">
              <span className="label">On this page</span>
              <ol>
                {SECTIONS.map(({ id, nav }) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      className={activeId === id ? 'active' : undefined}
                      aria-current={activeId === id ? 'location' : undefined}>
                      {nav}
                    </a>
                  </li>
                ))}
              </ol>
            </StyledToc>

            <StyledSections>
              <section id="overview">
                <h2>The Problem</h2>
                <p>
                  A primary goal of current machine learning research is to achieve artificial
                  general intelligence (AGI), and it is being pursued mainly in three ways:
                </p>
                <ul className="points">
                  <li>
                    <span className="key">Scaling laws:</span> training larger models on more data.
                  </li>
                  <li>
                    <span className="key">Engineering practice:</span> wrapping those models in
                    agent loops so they can call tools and read their own output.
                  </li>
                  <li>
                    <span className="key">Architectural exploration:</span> refining the transformer
                    architecture, or exploring others, such as world models and JEPA.
                  </li>
                </ul>

                <p>While these approaches differ, they in fact rest on one shared core bet:</p>

                <Quote>
                  General intelligence is achievable from the same kind of approach that produced
                  the narrow intelligence we have today, differing from it only in scale, structure,
                  and scaffolding.
                </Quote>

                <p>
                  The bet is understandable. Intelligence of a kind has advanced this way in front
                  of us, and one theoretical result is read as support for it. The universal
                  approximation theorem (UAT) states that a wide enough network can approximate any
                  continuous function on a <span className="key">bounded domain</span> to any
                  accuracy required.
                </p>

                <p>
                  However, there is one caveat. The bet carries an assumption that is rarely stated:
                </p>

                <Quote>
                  The gap between today&rsquo;s systems and general intelligence is a matter of
                  degree, not of kind.
                </Quote>

                <p>The two readings lead to different work:</p>
                <ul className="points">
                  <li>
                    <span className="key">Degree.</span> Everything needed is already present, and
                    the remaining task is to supply more of it: more parameters, more data, more
                    compute.
                  </li>
                  <li>
                    <span className="key">Kind.</span> No amount of any of those closes the gap,
                    because what is missing was never there in a smaller form. It has to be
                    identified and built, and the only way to identify it is to know what the target
                    is made of.
                  </li>
                </ul>

                <p>
                  People do notice this, and alternatives are proposed: Fei Fei Li&rsquo;s world
                  models, Yann LeCun&rsquo;s JEPA. These are well specified and seriously built, and
                  they share one move. Each identifies a feature that human intelligence has, a
                  model of the world or prediction inside a representation space, and designs an
                  architecture around it. 
                </p>  
                <p>
                  Yet,{' '}
                  <span className="key">
                    a feature that general intelligence requires is not thereby a feature that
                    produces it
                  </span>
                  . That AGI needs to understand the world does not mean understanding the world is
                  enough to reach it, and neither programme says what the remaining conditions are.
                  So the architecture is fixed in advance and the intelligence is expected to emerge
                  inside it, which leaves the same question standing:
                </p>

                <Quote>What is intelligence, actually?</Quote>

                <p>
                  This work is an attempt to answer that question, and doing so{' '}
                  <span className="key">reverses the direction of the search</span>. Trying one
                  approach after another and testing whether it arrives is searching for a
                  destination nobody can see.{' '}
                  <span className="key">
                    Describing the destination first is what makes it possible to reason about which
                    route could reach it
                  </span>
                  , rather than sampling routes and hoping.
                </p>

                <Figure
                  label="Figure 1."
                  caption="The destination is the same object in both panels and sits in the same place. The only thing that changes is whether it has been described.">
                  <FigureSearchDirection />
                </Figure>

                <h3>The One Guaranteed Starting Point</h3>
                <p>
                  What we are looking for is <span className="key">human level intelligence</span>.
                  Without knowing what human intelligence is, nobody can predict whether the
                  approaches above will ever reach it, and the only way left to find out is to spend{' '}
                  <span className="key">billions</span> and see. The only system we know of that reaches human level intelligence
                  is <span className="key">human cognition</span>, as it is the source of the
                  definition itself, and every person reading this is running an instance of it.
                </p>
                <p>
                  Intelligence is not a separate faculty that a mind has alongside everything else.
                  It is a <span className="key">product of how that mind is structured</span>, so a
                  structural account of cognition is also an account of the intelligence it
                  produces. That is the object of this work: to give a structural account of
                  cognition, rather than another semantic description of intelligence.
                </p>

                <Quote>
                  A model that reproduces the necessary structural features of the cognitive process
                  attains the kind of intelligence that structure gives rise to.
                </Quote>

                <p>
                  This claim is narrow on purpose. It does not say that a structure resembling
                  cognition is required for intelligence in general. Other kinds of intelligence
                  exist, and what current models have is one of them. It says only that for{' '}
                  <span className="key">human level</span> intelligence specifically, the one route
                  known to arrive is the one running through a structure aligned with human
                  cognition, because it is the only route with a{' '}
                  <span className="key">working instance</span> at the end of it.
                </p>
                <p>
                  Taking that route is not a matter of preference. Working through the structure of
                  human cognition reveals features that human intelligence depends on but{' '}
                  <span className="key">deep learning does not support</span>, and those missing features correspond to the
                  difficulties deep learning keeps meeting: the amount of data a model needs, the
                  absence of any internal structure to interpret, generalization that does not
                  survive a change of context, the inability to build one concept on top of another,
                  and the loss of what was learned before whenever something new is learned.
                </p>

                <h3>The Modeling Target</h3>
                <p>
                  Cognition is an emergent property of the brain, and emergence means the properties
                  of the parts are insufficient to predict the properties of the whole. That has a
                  consequence for how the problem should be attacked, and it is{' '}
                  <span className="key">
                    the point on which this work differs from everything else in the field
                  </span>
                  .
                </p>
                <p>
                  Every mainstream approach works on the container and waits for the emergent
                  structure to appear inside it. Architectures are proposed, scaled and patched, and
                  general intelligence is expected to arrive from one of them. But if the properties
                  of the parts are insufficient to predict the properties of the whole, then{' '}
                  <span className="key">
                    searching over containers carries no information about which container produces
                    the structure that is wanted
                  </span>
                  .
                </p>
                <p>
                  Some of the foundations of current machine learning do reference the brain, the{' '}
                  <span className="key">perceptron</span> most obviously, but that is no guarantee
                  that the same emergence follows. It is entirely possible that on such a{' '}
                  <span className="key">family of architectures</span> it just never happens.
                </p>
                <p>
                  This work runs in the opposite direction. What is modelled is{' '}
                  <span className="key">the emergent structure itself</span>, and the container is
                  designed afterward to support it. The alignment is in abstract structure, not in
                  biology.
                </p>

                <Callout label="Analogy">
                  <p>
                    An application is the same application on Windows, on macOS and on a phone. The
                    code compiled for each of them is different, and what makes all three the same
                    application is <strong>the abstract structure</strong>: the states it can be in,
                    and the linkage between them. The structure is what identifies it, and the code
                    is one way of carrying that structure.
                  </p>
                </Callout>

                <p>
                  Cognition stands in the same relation to the brain. Once the structure is the
                  thing being built, the container can be designed to fit its running, and the
                  modelling of that structure{' '}
                  <span className="key">should not be constrained in advance</span> by an assumed
                  container, which is what the current machine learning paradigm would be.
                </p>
                <p>
                  So this work does not assume anything that is taken for granted under the current
                  paradigm, including <span className="key">deep learning</span>, and instead
                  reasons from first principles and from phenomena that are actually observed.
                </p>

                <h3>A Definition of AGI</h3>
                <p>
                  AGI means different things to different people. An argument that turns on which
                  meaning a term carries is an argument about the word rather than about what it
                  refers to, so the convention here is to state what a term means before using it.
                </p>
                <p>
                  As mentioned above,{' '}
                  <span className="key">
                    the only thing we can confirm to have reached general intelligence is a person
                  </span>
                  , so the definition of AGI used here is anchored to that and is deliberately
                  conservative:
                </p>

                <Quote>
                  A model whose structure reproduces the abstract structure of human cognition,
                  thereby attaining human level intelligence, while shedding the limits of the
                  biological implementation: brain size, lifespan, clock rate, one fixed body.
                </Quote>

                <p>
                  The second half follows from the first rather than being added to it. Once the
                  structure is the thing being built, rather than a set of weights being trained
                  until a score improves, the internal state can be read while it runs, edited,
                  copied, forked and rejoined, and run at whatever rate the hardware allows. None of
                  that is available in a brain, and none of it needed a separate assumption.
                </p>
              </section>

              <section id="method">
                <h2>Methodology</h2>
                <p>
                  Neurons are the wrong level at which to read the structure of cognition, for the
                  reason given above, which is why decades of work on neural mechanism have not
                  produced a derivation of it. This work models cognition at its own level and
                  treats neuroscience as corroboration rather than as the foundation.
                </p>
                <p>
                  That choice is what makes the model{' '}
                  <span className="key">independent of what it runs on</span>, and everything after
                  it depends on that being true. If structure is what constitutes cognition, then
                  any platform that can carry the structure carries cognition, and a machine
                  learning implementation is a real possibility rather than a metaphor.
                </p>

                <h3>The Thing Being Modelled</h3>
                <p>
                  As few assumptions as possible are made about what is being modelled, which
                  matters most where the terms are vague enough that no universally agreed
                  definition exists. The minimal structure is a coupled pair.
                </p>
                <div className="definitions">
                  <div className="definition">
                    <span className="definition-name">Cognition</span>
                    <p>
                      Everything with the property of memory: knowledge, values, personality,
                      reactions.
                    </p>
                  </div>
                  <div className="definition">
                    <span className="definition-name">Awareness</span>
                    <p>What is active at this instant: sensation, feeling, thought.</p>
                  </div>
                </div>
                <p>The pair is coupled in both directions.</p>
                <ul className="points">
                  <li>
                    Cognition decides how awareness moves when nothing is arriving from outside.
                    <Note>
                      <p>
                        Two people meeting the same event think along different paths, because what
                        each of them holds in cognition differs.
                      </p>
                    </Note>
                  </li>
                  <li>
                    The path awareness takes reshapes cognition.
                    <Note>
                      <p>You remember what you were just thinking about.</p>
                    </Note>
                  </li>
                </ul>

                <Callout label="Thought experiment">
                  <p>
                    Take a question that is simple but not immediate. Asked before you have thought
                    about it, you do not know the answer. Think it through, then wait long enough
                    that you are no longer holding the answer in mind. Asked again, you answer it.
                    The question was the same both times and the response was not, so{' '}
                    <strong>something in you changed, and thinking is what changed it.</strong>
                  </p>
                </Callout>

                <h3>Two ways of observing, and why both are needed</h3>
                <p>
                  The object of study is subjective, and that creates a specific difficulty:
                  everything objective that can be measured about a mind is already a projection of
                  it.
                </p>
                <ul className="points">
                  <li>
                    <span className="key">First person.</span> Introspection is the only instrument
                    that reaches inside a subjectivity at all. It gives a detailed account of what
                    depends on what, what happens in what order, and what rearranges what. What it
                    cannot give is reproducibility, so it is used as a source of candidates and
                    never as a source of conclusions. It proposes; formalisation decides which
                    proposals survive.
                  </li>
                  <li>
                    <span className="key">Third person.</span> Behaviour across many people,
                    development from infancy, variation across cultures and languages, and the
                    phenomena psychology has catalogued. Reproducible, because anyone can look. But
                    each observation is a reading taken from outside a closed system, so the
                    structure that produced it has to be inferred rather than seen.
                  </li>
                </ul>
                <p>
                  One instance in high detail, constrained by many instances observed from outside.
                  A structure derived from the first has to <span className="key">explain</span> the
                  phenomena of the second across people whose cognitive contents differ completely,
                  and any phenomenon it cannot explain forces a revision rather than an exception.
                </p>

                <h3>What can actually be measured</h3>
                <p>
                  Nothing is assumed about the inside. A person&rsquo;s cognition is not open to
                  inspection, so the only honest move is to treat it as closed and{' '}
                  <span className="key">describe it by what it does under test</span>. Ask a
                  question and what comes back is one reading: not the state itself, but a boundary
                  the state has to sit inside.
                </p>
                <p>
                  A single reading says little. Put readings in series and they say a great deal,
                  because what is then being watched is not an answer but a change. How far the
                  responses agree with one another across repeats. How that agreement moves after
                  something is learned. How long the reaction takes, and whether the delay shortens
                  with exposure. Each probe narrows the range of structures that could have produced
                  the whole sequence, and a structure is admitted only if it can produce every
                  reading in it.
                </p>

                <Eq note="r is a reading and Λ the set of structures still admissible after n of them. The set only ever shrinks, so a phenomenon that no surviving structure explains is not an exception to be noted; it empties the set and the structure has to be rebuilt.">
                  <EqLine>
                    <span className="stmt">
                      <i>Λ</i>
                      <sub>
                        <i>n</i>
                      </sub>{' '}
                      = {'{'} <i>C</i> : <i>C</i> explains <i>r</i>
                      <sub>1</sub>, &#8230;, <i>r</i>
                      <sub>
                        <i>n</i>
                      </sub>{' '}
                      {'}'}
                    </span>
                    <span className="sep" />
                    <span className="stmt">
                      <i>Λ</i>
                      <sub>
                        <i>n</i>+1
                      </sub>{' '}
                      <span className="op">&#8838;</span> <i>Λ</i>
                      <sub>
                        <i>n</i>
                      </sub>
                    </span>
                  </EqLine>
                </Eq>

                <h3>Why a hypergraph</h3>
                <p>
                  A description of what a mind holds needs a formalism to be written in. Ontology,
                  the study of what exists and how it is categorised, hands you substances and the
                  relations between them. Modelled directly that becomes a typed directed graph,
                  where things are nodes and relations are edges, and it brings two problems.
                  Relations have to be a different kind of object from the things they relate, so
                  they need machinery of their own. And there is nowhere to put a relation{' '}
                  <em>between two relations</em>, though those plainly exist: over the real numbers,
                  is greater than and is less than are mutually exclusive, and that exclusivity is
                  itself a relation holding between two relations.
                </p>
                <p>
                  A directed hypergraph removes the distinction between the two kinds of object.
                  Anything that can interact with anything else is a hypernode, relations included,
                  and a hyperedge points from a set of hypernodes to a set of hypernodes with{' '}
                  <span className="key">no internal structure of its own</span>. A relation R
                  holding from A to B is the edge from the set containing A and R to the set
                  containing B, and relations between relations then follow without anything being
                  added. Everything is a primitive of the same kind, and the structure is free to
                  recurse.
                </p>

                <Eq note="V is the set of hypernodes, and everything the description contains is in it, relations included. A hyperedge is an ordered pair of subsets of V and carries nothing else, so the third line is the second line again with different members. E is a relation between relations, mutual exclusivity for instance.">
                  <EqLine>
                    <span className="stmt">
                      <i>e</i> = (<i>S</i>, <i>T</i>)
                    </span>
                    <span className="sep" />
                    <span className="stmt">
                      <i>S</i>, <i>T</i> <span className="op">&#8838;</span> <i>V</i>
                    </span>
                  </EqLine>
                  <EqLine>
                    <span className="stmt">
                      <i>R</i>(<i>A</i>, <i>B</i>) <span className="op">&#8801;</span> ( {'{'}
                      <i>A</i>, <i>R</i>
                      {'}'}, {'{'}
                      <i>B</i>
                      {'}'} )
                    </span>
                  </EqLine>
                  <EqLine>
                    <span className="stmt">
                      <i>E</i>(<i>R</i>
                      <sub>1</sub>, <i>R</i>
                      <sub>2</sub>) <span className="op">&#8801;</span> ( {'{'}
                      <i>R</i>
                      <sub>1</sub>, <i>E</i>
                      {'}'}, {'{'}
                      <i>R</i>
                      <sub>2</sub>
                      {'}'} )
                    </span>
                  </EqLine>
                </Eq>

                <Figure
                  label="Figure 2."
                  caption="The second and third panels are the same drawing. That is the argument for the formalism: the case a typed graph has no room for costs nothing here.">
                  <FigureHypergraph />
                </Figure>

                <p>
                  It also stays refinable. A hypernode can itself be a set of smaller hypernodes, so
                  a description can be made finer indefinitely without changing the formalism and
                  without rewriting what was already said at the coarser level.
                </p>

                <h3>What reasoning looks like in it</h3>
                <p>
                  The first test of a formalism is whether the things people already do with thought
                  can be written in it without special cases. Deduction, induction and abduction
                  turn out to be three ways of moving on one structure rather than three separate
                  faculties.
                </p>

                <h4>Deduction follows edges that already exist</h4>
                <p>
                  Take the syllogism: Socrates is a man, every man is mortal, therefore Socrates is
                  mortal. Both premises are written the same way the formalism writes everything
                  else. The first is the edge from the set containing Socrates and the relation{' '}
                  <em>is a</em> to the set containing man. The second is the edge from the set
                  containing man and the relation <em>implies</em> to the set containing mortal.
                  Deduction is following the one and then the other, and the conclusion is the
                  composite edge from Socrates and <em>is a</em> to mortal. It was reachable before
                  anyone stated it, which is the precise sense in which a valid deduction tells you
                  nothing you did not already have.
                </p>

                <Figure
                  label="Figure 3."
                  caption="Both premises as hyperedges, and the conclusion as the edge that composing them reaches. Nothing below the rule is new; it was already there to be walked to.">
                  <FigureDeduction />
                </Figure>

                <h4>Induction builds a hypernode that was not there</h4>
                <p>
                  Several cases carry the same edge: this swan is white, that swan is white, the
                  next one too. Induction forms a hypernode covering those cases and attaches the
                  edge to that instead of to each case separately. What is new is the hypernode, not
                  the edge, which is why induction adds something where deduction does not, and also
                  why induction can be wrong: the hypernode is a claim that those cases belong
                  together.
                </p>

                <Figure
                  label="Figure 4."
                  caption="Three cases carrying the same edge, and the hypernode induction forms so the edge can be attached once. The new object is the one on the left of the bottom row, not the arrow.">
                  <FigureInduction />
                </Figure>

                <h4>Abduction reads an edge from the wrong end</h4>
                <p>
                  The ground is wet; rain causes wet ground; so rain is proposed. The edge from rain
                  to wet ground is the one that already exists, and abduction enters at its
                  destination and names a source. The formalism says in one line why this is not
                  valid: an edge points from a set to a set, and several different sources can point
                  at the same destination, so running one backwards returns a candidate rather than
                  a conclusion. What makes one candidate better than another is how much else in the
                  structure it already connects to.
                </p>

                <Figure
                  label="Figure 5."
                  caption="The edge that exists, and the direction abduction reads it in. The dashed arrow is the move, and it is dashed because it is not an edge in the structure.">
                  <FigureAbduction />
                </Figure>

                <p>
                  The three are not equally basic, and the order between them matters for what
                  follows. Deduction moves through structure that already exists.{' '}
                  <span className="key">Induction is what puts that structure there</span>, and the
                  section on self assembly below is an account of how it happens with nobody
                  supplying the categories. Abduction is an existing edge read backwards, and it is
                  what produces the guesses induction then has to confirm or drop. A system that has
                  only the first of the three can rearrange what it was given and nothing else.
                </p>
                <p>
                  One more consequence, because it connects the formalism to the rest of the page. A
                  deduction walked often enough stops being walked: the composite edge from Socrates
                  to being mortal becomes an edge in its own right, and the two steps that produced
                  it are no longer traversed. That is the operation Figure 6 draws, and it is what
                  it feels like to know something immediately and be unable to say how you got
                  there.
                </p>
              </section>

              <section id="paradigm">
                <h2>Where deep learning cannot follow</h2>
                <p>
                  One assumption sits under every mainstream model whatever its architecture: that
                  intelligence is the approximation of a mapping from a start state to an end state.
                  Supervised models map inputs to labels. Diffusion and flow matching look like a
                  departure from that, but they only move the two endpoints, with noise as the start
                  state and a sample from the data distribution as the end state. Computation is
                  defined as transporting a given input toward a given target.
                </p>

                <Eq note="Expectation over a given data distribution, and L a given loss. The pair the model is asked to relate and the standard it is held to both enter from outside it.">
                  <EqLine>
                    <span className="stmt">
                      <i>f</i>
                      <sub>
                        <i>θ</i>
                      </sub>{' '}
                      : <i>X</i> <span className="op">&#8594;</span> <i>Y</i>
                    </span>
                    <span className="sep" />
                    <span className="stmt">
                      <i>θ</i>
                      <sup>&#8727;</sup>
                      <span className="op">=</span> arg min
                      <sub>
                        <i>θ</i>
                      </sub>{' '}
                      E [ L( <i>f</i>
                      <sub>
                        <i>θ</i>
                      </sub>
                      (<i>x</i>), <i>y</i> ) ]
                    </span>
                  </EqLine>
                </Eq>

                <p>
                  Cognition does not have that shape. It runs continuously in time and carries its
                  own state, with nothing resetting it between one input and the next. An input
                  arriving and an output being produced are two separate events happening against an
                  internal evolution that was already under way, and people do not take input{' '}
                  <em>in order to</em> produce output. Binding the two into a single mapping is an
                  artefact of the assumption rather than something observed.
                </p>

                <Eq note="ψ is the state now, u what is arriving, C the structure it runs on. No y appears in it and no loss appears in it. The second line is the part a mapping cannot have: the structure is itself a variable of the system, rewritten by the trajectory that runs on it. Naming F and G is the work.">
                  <EqLine>
                    <span className="stmt">
                      d<i>ψ</i>/d<i>t</i>
                      <span className="op">=</span>
                      <i>F</i>( <i>ψ</i>, <i>u</i>(<i>t</i>) ; <i>C</i> )
                    </span>
                  </EqLine>
                  <EqLine>
                    <span className="stmt">
                      d<i>C</i>/d<i>t</i>
                      <span className="op">=</span>
                      <i>G</i>( <i>C</i>, <i>ψ</i> )
                    </span>
                  </EqLine>
                </Eq>

                <h3>Why approximation results do not settle it</h3>
                <p>
                  A universal approximation theorem says that a wide enough network can approximate
                  any continuous function, and that result is the formal comfort underneath a great
                  deal of confidence in scale. Stated properly it is an existence result about one
                  fixed function.
                </p>

                <Eq note="For every continuous f on a compact K and every ε > 0, some network g satisfies this. Nothing in it says how g is found, and f does not move while it is being found.">
                  <EqLine>
                    <span className="stmt">
                      sup
                      <sub>
                        <i>x</i> &#8712; <i>K</i>
                      </sub>{' '}
                      | <i>f</i>(<i>x</i>) <span className="op">&#8722;</span> <i>g</i>(<i>x</i>) |{' '}
                      <span className="op">&lt;</span> <i>ε</i>
                    </span>
                  </EqLine>
                </Eq>

                <p>Two things sit outside what that result covers.</p>
                <ul className="points">
                  <li>
                    <span className="gap">The function it approximates is static.</span> Cognition
                    is not a fixed mapping being estimated more and more closely. The object here is{' '}
                    <MathText>
                      <i>f</i>
                      <sub>
                        <i>t</i>
                      </sub>
                    </MathText>
                    , rewritten every second by its own operation, so getting within{' '}
                    <MathText>
                      <i>ε</i>
                    </MathText>{' '}
                    of the version present at one moment says nothing about the next one, and the
                    sequence of approximations converges on nothing.
                  </li>
                  <li>
                    <span className="gap">Recurrence puts structure at every scale.</span> When a
                    state routes back through structure that its own passage has just altered, the
                    result has detail at arbitrarily fine scale rather than a fixed smoothness.
                    Approximating something like that with a fixed layered form takes capacity that
                    grows far faster than the description of it does, which is one reason scale
                    keeps buying less than expected. A model whose shape is unlike the shape of what
                    it is approximating does not get close to it cheaply at any size.
                  </li>
                </ul>

                <h3>What follows in training</h3>
                <p>
                  Layered structure forces a backward pass to assign credit, and backpropagation is
                  the method almost universally used for it. Any gradient needs an objective, and
                  the objective has to come from somewhere. The deeper commitment is that{' '}
                  <span className="key">the feedback comes from outside the system</span>: a label
                  in supervised learning, a reward function in reinforcement learning, a target
                  distribution in a generative model. Fixed, external, and unaffected by whatever
                  the model does.
                </p>
                <p>
                  In a person only a small set of bodily signals are given from birth. Everything
                  else that feels good or bad is built on top of those, which is to say it is
                  learned.
                </p>

                <Callout label="Observation">
                  <p>
                    An adult who spills a drink gets a negative signal, through the mess and the
                    scolding they can already anticipate. A small child can get a positive one,
                    because the spill is funny. Same event, opposite feedback.{' '}
                    <strong>
                      What decides the sign is the structure each of them already holds, and the
                      resulting feedback is what shapes that structure next.
                    </strong>{' '}
                    The feedback is inside the loop it drives.
                  </p>
                </Callout>

                <p>
                  <span className="gap">
                    That follows from the assumption rather than being an engineering choice
                  </span>
                  : a mapping needs a target, and a target has to be specified from outside. Methods
                  that do without gradients keep the commitment anyway. Forward Forward&rsquo;s
                  goodness function, predictive coding&rsquo;s prediction target and an evolutionary
                  method&rsquo;s fitness score are all supplied by a designer. The thing a mind
                  builds first is the one thing the model is never allowed to build.
                </p>

                <h3>What follows in architecture</h3>

                <Callout label="Observation">
                  <p>
                    When an adult man is fired, how does he react? One possible reaction is
                    immediate anxiety. But why? Why would the concept of &ldquo;being fired&rdquo;
                    link to &ldquo;anxiety&rdquo; at all? That link has to be assembled out of
                    smaller pieces, and one possible set of them is: being fired means unemployment,
                    unemployment means no money, no money means no resources, no resources means
                    suffering, and suffering he can foresee triggers anxiety. These pieces join into
                    the full path, and{' '}
                    <strong>
                      the full path in turn forms a shortcut that carries the quick reaction
                    </strong>
                    . When the reaction runs through that shortcut, the steps of the original long
                    path are not involved.
                  </p>
                  <p>
                    To say the long path is compressed into a direct one would suggest the original
                    was lost, and it is not. The long path is weakened, and the direct path forms
                    alongside it. Compression shows itself most where the long path is activated as
                    a whole and activated often: such a path usually has conditions that all have to
                    hold before it can be traversed end to end, so traversing it once means every
                    one of them was active at the same time. Replacing it with a shortcut saves
                    capacity, which frees space to learn more. You can study calculus because
                    arithmetic no longer costs you anything to run, though it cost you a great deal
                    when you first learned arithmetic. Every layer of what you understand rests on a
                    layer that has already dropped out of conscious reach.
                  </p>
                </Callout>

                <Figure
                  label="Figure 6."
                  caption="Path compression. The middle steps are not deleted; they stop being on the way, which is why an expert can do the thing and not say how.">
                  <FigurePathCompression />
                </Figure>

                <p>
                  The structure of a network is fixed before training begins. Training changes the
                  weights; the topology along which information flows never rearranges. Residual
                  connections are wiring a designer chose, not a rerouting the system performed.{' '}
                  <span className="gap">
                    A feature holds a fixed relation to the features it connects to, so it is bound
                    to its layer
                  </span>{' '}
                  and cannot drift, merge with a neighbour, or split in two. A long path traversed
                  many times never becomes a short one.
                </p>

                <p>
                  It also means the structure cannot be fixed in advance. When a path shortens, the
                  manifolds the states lie on drift, merge and rearrange. The point is sharper for a
                  neural network, where each layer&rsquo;s latent space is itself a manifold rather
                  than a single point standing for a single state. A retrieval store attached to the
                  side is not the same thing either: intuition has to live inside the structure,
                  because it is a change to the structure.
                </p>

                <p>
                  The consequence is about dependency. A simple idea and a harder one built on top
                  of it have a relationship, but a trained network holds no structural dependency
                  between them, so the harder one cannot be learned <em>through</em> the simpler
                  one. Before a person understands the simple thing, the hard thing arrives closer
                  to noise than to information. Once they hold the simple thing, the hard thing is
                  processed through it first and arrives as a pattern, carrying far less that is
                  new.
                </p>

                <Callout label="Observation">
                  <p>
                    A Chinese sentence takes a Mandarin speaker one or two hearings to repeat back.
                    A Hindi sentence of the same length takes them dozens, and the difference is not
                    in their memory. In the first case the structure they already hold filters sound
                    into phonetics and phonetics into meaning, and every step strips entropy out, so
                    what finally has to be held is small and almost entirely regular. In the second
                    case the chain stops at phonetics. What is left has no regularity the listener
                    can exploit, so it arrives as <strong>noise</strong>, and noise has to be stored
                    close to the form it came in.
                  </p>

                  {/* Numbered and captioned like any other figure, and inside the box because
                      it draws this observation rather than a claim in the argument. */}
                  <Figure
                    label="Figure 7."
                    caption="The same two sentences through two structures. Each step down is entropy the structure was able to remove, and the step that cannot be taken is what leaves the rest as noise.">
                    <FigureFiltering />
                  </Figure>

                  <p>
                    <strong>
                      How much information something carries is not a property of the thing. It is
                      relative to the structure receiving it.
                    </strong>
                  </p>
                </Callout>

                <Eq note="H(x | C) is what is left to hold once the structure C has taken out everything it can account for. Learning is what moves it: the same x, a different receiver, less to store. It is why data efficiency is downstream of structure rather than something to be optimised for on its own.">
                  <EqLine>
                    <span className="stmt">
                      <i>H</i>( <i>x</i> | <i>C</i>&#8242; ) <span className="op">&lt;</span>{' '}
                      <i>H</i>( <i>x</i> | <i>C</i> )
                    </span>
                  </EqLine>
                </Eq>

                <Callout label="Observation">
                  <p>
                    Someone who has not absorbed arithmetic cannot study calculus at all.{' '}
                    <strong>
                      New understanding attaches to structure that has already been compressed
                    </strong>
                    , which is what makes knowledge cumulative rather than merely accumulated.
                  </p>
                </Callout>

                <p>
                  A latent space is not a substitute for that. It does not change over time, and the
                  relations between features are folded into the geometry of the coordinates rather
                  than held as structure that can be revised.
                </p>

                <h3>What follows in theory</h3>
                <p>
                  Learning theory speaks in distributions, convergence rates, generalisation bounds
                  and scaling laws, all of which are statistical descriptions of a model seen from
                  outside it. None of them says how intelligence forms or how it changes over time.
                </p>
                <p>
                  The theories of consciousness have the mirror image of that problem. Global
                  Workspace Theory describes an arrangement, a broadcast and a spotlight, without
                  saying what the workspace is made of or how it comes to exist. Integrated
                  Information Theory assigns a quantity to a system without giving a structure that
                  says how any of it forms, evolves or breaks. Both describe a result. Neither gives
                  a mechanism anyone could build from.
                </p>
                <p>
                  The black box problem is the same absence seen from the inside.{' '}
                  <span className="gap">
                    When every parameter is shaped by gradient flow there is no internal structure
                    left to interpret
                  </span>
                  . Opacity is not a missing tool that better probes would supply. It is the default
                  state of a system that was never given a structural organisation in the first
                  place.
                </p>

                <h3>What it costs in capability</h3>
                <p>
                  Two abilities that define human intelligence are absent, and both follow from what
                  is above rather than being separate shortcomings. The first is{' '}
                  <span className="key">continual learning</span>. New knowledge competes with old
                  knowledge instead of building on it, because nothing can be placed anywhere
                  structurally and everything enters as undifferentiated gradient flow. Catastrophic
                  forgetting is the visible symptom.
                </p>
                <p>
                  The second is <span className="key">thinking</span>, and it is the one that is
                  rarely counted as a capability at all.
                </p>

                <Callout label="Thought experiment">
                  <p>
                    Sit with something you already know and ask why it is so. Ask how the things you
                    know relate to one another. Work out an answer to a problem nobody has answered
                    for you. No new information arrives, nobody tells you anything, and afterwards
                    you understand it differently and can do more with it than before.{' '}
                    <strong>The change stays</strong>: next time you no longer have to work it out,
                    which is what makes it learning rather than a passing thought.
                  </p>
                </Callout>

                <p>
                  What happened is that knowledge you were holding as separate pieces was taken
                  apart and put back together with different connections between them. It is the
                  operation that separates understanding something from having memorized it, and it
                  costs no data at all. What it costs is thinking, and the time thinking takes.
                  Current models go the other way: to become more intelligent they need more data.
                </p>
                <p>
                  A model can be given somewhere to write into, a retrieval store for instance, and
                  that is still not the same thing. Even with somewhere to write, a model needs data
                  to learn from, some form of input and usually a target to move toward. Thinking in
                  a person is an internal process. It involves no interaction with the external
                  world at all.
                </p>
              </section>

              <section id="emergence">
                <h2>Structure that assembles itself</h2>
                <p>
                  Before anything can be related to anything else, there has to be a thing there to
                  relate. Ontology asks what exists; the question before it is how a mind comes to
                  have anything at all that it can point at. Not how a category is learned once
                  someone hands you the categories, but how something becomes recognisable when
                  nothing yet plays that role.
                </p>
                <p>
                  A representation is not an object placed somewhere. It is what is left when the
                  spread of where a state can go next{' '}
                  <span className="key">collapses from high uncertainty to low</span>, continuously,
                  until a region of states is reliable enough to be treated as one thing.
                </p>

                <Eq note="A region ρ has become a thing exactly when being inside it says where the state goes next. Nothing named ρ, selected it or rewarded it; it is what is left when the spread collapses, and the collapse is the definition rather than the evidence for it.">
                  <EqLine>
                    <span className="stmt">
                      <i>H</i>( <i>A</i>
                      <sub>
                        <i>t</i>+1
                      </sub>{' '}
                      | <i>A</i>
                      <sub>
                        <i>t</i>
                      </sub>{' '}
                      &#8712; <i>ρ</i> ) <span className="op">&#8810;</span> <i>H</i>( <i>A</i>
                      <sub>
                        <i>t</i>+1
                      </sub>{' '}
                      )
                    </span>
                  </EqLine>
                </Eq>

                <ul className="numbered">
                  <li>
                    <span className="numbered-title">
                      Situations arrive that share part of a pattern
                    </span>
                    Two situations are not identical but resemble each other, and the relation
                    between their parts is similar in both. Physical time is continuous, so what
                    arrives is continuous too.
                  </li>
                  <li>
                    <span className="numbered-title">
                      The shared part is reinforced on every pass
                    </span>
                    Each situation activates a region of states that partly overlaps with the region
                    the other one activates. The overlapping part is present every time. The parts
                    that vary are present occasionally.
                  </li>
                  <li>
                    <span className="numbered-title">Only the shared part survives</span>
                    The link from one overlap to the next is reinforced on every exposure, while the
                    links off the overlap are reinforced rarely and stay weak. What is left standing
                    is the structure the situations had in common.
                  </li>
                  <li>
                    <span className="numbered-title">That is the representation</span>
                    Nothing selected it, labelled it or rewarded it. It precipitated out. No
                    feedback of any kind was involved, which makes this unsupervised in a way that
                    no pretext task is.
                  </li>
                </ul>

                <Figure
                  label="Figure 8."
                  caption="The four steps above, drawn. Nothing selects the region and nothing labels it; it is what survives being reinforced on every pass while everything around it is not.">
                  <FigureSelfAssembly />
                </Figure>

                <p>
                  This is induction happening physically rather than being applied as a rule. The
                  region that survives is the hypernode that Figure 4 shows induction forming, and
                  nothing had to supply the category it stands for.
                </p>
                <p>
                  The starting conditions are close to nothing:{' '}
                  <span className="key">
                    continuity in what arrives, so that similar situations produce similar states
                  </span>
                  , and some initial pathways carrying that input inward. Given those two, structure
                  deepens on its own, and{' '}
                  <span className="key">
                    each layer that forms becomes the material the next one is built from
                  </span>
                  . That recursion is the whole engine, and it is why the process needs no designer
                  to say what the features should be at any level.
                </p>

                <Callout label="Observation">
                  <p>
                    A newborn does not have vision. It has stimulation on the optic nerve. Spatial
                    vision and the identifiability of objects are not delivered with the hardware:
                    they assemble out of how that stimulation changes as the world moves and as the
                    infant moves.
                  </p>
                </Callout>

                <Callout label="Observation">
                  <p>
                    People who have only ever lived among faces of one population genuinely struggle
                    to tell faces of another apart, and the ability arrives after living among them.
                    The finer representation had not formed, and then it did. The same happens with
                    the sounds of a language you do not speak: you start recognising its phonetic
                    shape well before you understand a word of it.
                  </p>
                </Callout>

                <h3>
                  Why unsupervised, reinforcement and supervised learning are not three algorithms
                </h3>
                <p>
                  They are three boxes a designer chooses between before training starts. In a
                  person they are stages of one process, and the order between them is a{' '}
                  <span className="key">dependency</span> rather than a curriculum: each stage
                  cannot run until the one before it has produced what it needs.
                </p>

                <ul className="numbered">
                  <li>
                    <span className="numbered-title">Features form, with no feedback at all</span>
                    The self assembly described above. Structure precipitates out of the temporal
                    structure of what arrives. Nothing is being optimised and nothing is being told.
                  </li>
                  <li>
                    <span className="numbered-title">Routing between features gets laid down</span>
                    Once features exist, the temporal relations between them are learned: this
                    region tends to be followed by that one. Some of those chains terminate at the
                    bodily signals given from birth. A feature several steps away from anything
                    biological therefore acquires a good or bad sense through the chain it sits on.
                    This is learning the causal relations between features, which is a separate
                    thing from feedback then acting on the route.
                  </li>
                  <li>
                    <span className="numbered-title">Only then can anything be supervised</span>
                    Being taught requires that <em>my answer matches the given answer</em> is itself
                    a state the learner can identify, and that identifying it already produces
                    something positive or negative. The second of those is built from experience:
                    answering correctly earns a sweet, and the child has already connected a sweet
                    to the taste of sugar, so answering correctly inherits that feedback along the
                    route. Both have to be in place first. Before that, a teacher&rsquo;s reaction
                    carries no weight at all, every outcome is worth the same, and nothing
                    converges.
                  </li>
                </ul>

                <Callout label="Observation">
                  <p>
                    A child cannot learn that 2 + 2 = 5 is wrong until the teacher&rsquo;s reaction
                    is something that can affect them. If it cannot, no amount of correction lands.{' '}
                    <strong>
                      The ability to be taught is itself learned, and it is learned last.
                    </strong>
                  </p>
                </Callout>
              </section>

              <section id="neighbours">
                <h2>Where it sits</h2>
                <p>
                  This is not supervised, unsupervised or reinforcement learning. That three way
                  split classifies approaches to function approximation, and this is not one of
                  them, which is as much a problem as a distinction: no existing benchmark is
                  waiting for it. The nearest work, and where it parts company with each:
                </p>
                <Comparison rows={NEIGHBOURS} className="comparison" />
                <p className="after-block">
                  Locality, forward only training and a recurrent state of constant size are
                  properties a good deal of this field already shares. None of them is presented
                  here as an invention.
                </p>

                <h3>What comes with it</h3>
                <p>
                  Because the framework describes cognition rather than a model architecture, it
                  reaches things machine learning normally has no contact with. A great many
                  psychological phenomena follow from the same small set of structural rules,
                  without a separate account for each: how a preference forms, why views on one
                  subject diverge systematically between people, why a learned reaction persists
                  after its original cause has become untraceable. The same holds for several long
                  standing questions in philosophy, which turn out to be questions about the
                  structure doing the asking.
                </p>
              </section>

              <section id="status">
                <h2>Where it stands</h2>
                <p>
                  The present work is the theory: the structural observations, the formal objects
                  that carry them, and whether the whole stays consistent while covering more of
                  what is actually observed in people. That is a demanding bar. Every phenomenon the
                  framework cannot produce forces a structural revision rather than an exception,
                  and it has been rebuilt more than once on that basis.
                </p>
                <p>
                  What it describes is <span className="key">implementable</span> rather than an
                  abstraction with no route to hardware. Updates are local in time and space, the
                  computation is purely forward, and it maps onto parallel, distributed and analog
                  hardware rather than requiring a global backward pass.
                </p>
                <div className="placeholder-wrap">
                  <Placeholder status="On the way">
                    Implementation notes and results, together with the falsification targets: the
                    predictions where this framework and the alternatives say measurably different
                    things.
                  </Placeholder>
                </div>
              </section>

              <section id="collaborate">
                <h2>Work with me</h2>
                <p>
                  I work on this full time and independently. The most useful thing anyone can send
                  is an objection. If you work on anything adjacent, whether local learning rules,
                  dynamical systems accounts of cognition, computational cognitive science or
                  philosophy of mind, I would like to hear where you think this breaks.
                </p>
                <p>
                  If you invest in early foundational work, there is considerably more detail than
                  belongs on a public page, including the parts held back here, and I am glad to go
                  through it properly.
                </p>
                <div className="contact-actions">
                  <a className="primary" href={`mailto:${email}`}>
                    Say hello
                  </a>
                  <span className="contact-email">{email}</span>
                </div>
              </section>
            </StyledSections>
          </StyledBody>
        </div>
      </StyledResearchPage>
    </Layout>
  );
};

ResearchPage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default ResearchPage;
